import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Info } from 'lucide-react';
import { ONLINE_SCHEDULE, INPERSON_SCHEDULE, DROPIN_CLASSES } from '../utils/constants';
import useSEO from '../hooks/useSEO';
import './Schedule.css';

const ALL_TIMES_INPERSON = ['5:00am', '6:00am', '7:00am', '6:00pm', '7:15pm'];
const ALL_TIMES_ONLINE = ['5:00am', '6:00pm'];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

export default function Schedule() {
  const eventSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'EventSeries',
    name: 'The Fhysique Factory Weekly Training Sessions',
    location: {
      '@type': 'Place',
      name: 'The Fhysique Factory',
      address: { '@type': 'PostalAddress', streetAddress: '6-8 Parkington Plaza, Maths Unlimited Building', addressLocality: 'Kingston', addressCountry: 'JM' },
    },
    organizer: { '@type': 'LocalBusiness', name: 'The Fhysique Factory', url: 'https://fysiquefactory.fit' },
    description: 'Weekly personal training sessions including online sessions, in-person training, Step class, Soca Abs, and Powerlifting at Parkington Plaza Kingston Jamaica.',
    subEvent: [
      { '@type': 'Event', name: 'Online Training Session — Morning', startDate: 'T05:00', description: 'Live online training Mon/Tue/Thu/Fri at 5am' },
      { '@type': 'Event', name: 'Online Training Session — Evening', startDate: 'T18:00', description: 'Live online training Mon/Tue/Thu at 6pm' },
      { '@type': 'Event', name: 'In-Person Training — Parkington Plaza', startDate: 'T05:00', description: 'In-person sessions from 5am to 7:15pm, Mon-Sat' },
      { '@type': 'Event', name: 'Step Class (Drop-In)', startDate: 'T18:00', description: 'Wednesday 6pm Step class, $1,500 JMD per session' },
      { '@type': 'Event', name: 'Soca Abs (Drop-In)', startDate: 'T18:00', description: 'Thursday 6pm Soca Abs class, $1,500 JMD per session' },
      { '@type': 'Event', name: 'Powerlifting (Drop-In)', startDate: 'T07:00', description: 'Saturday 7am Powerlifting class, $1,500 JMD per session' },
    ],
  }), []);

  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://fysiquefactory.fit/' },
      { '@type': 'ListItem', position: 2, name: 'Schedule', item: 'https://fysiquefactory.fit/schedule' },
    ],
  }), []);

  useSEO({
    title: 'Gym Class Schedule Kingston Jamaica | Training Times & Drop-In Classes',
    description: 'Full weekly training schedule at The Fhysique Factory, Parkington Plaza Kingston. Morning 5am sessions, evening 6pm classes. Step, Soca Abs, Powerlifting drop-ins. Online & in-person session times for all fitness levels.',
    path: '/schedule',
    schema: [eventSchema, breadcrumbSchema],
  });

  return (
    <div className="page schedule-page">
      <section className="page-hero">
        <div className="container">
          <span className="section-label">Weekly Timetable</span>
          <div className="lime-line" />
          <h1 className="section-title">Training Schedule</h1>
          <p className="section-sub">
            Multiple time slots every week. Find what fits your lifestyle.
          </p>
        </div>
      </section>

      {/* Online Sessions */}
      <section className="schedule-section">
        <div className="container">
          <div className="sched-header">
            <div>
              <span className="section-label">Online Members</span>
              <div className="lime-line" />
              <h2 className="schedule-title">Online Sessions</h2>
              <p className="text-muted">Join from anywhere via video call. 1 hour per session.</p>
            </div>
            <div className="sched-note">
              <Info size={14} /> No online sessions on Wednesday & Sunday
            </div>
          </div>
          <div className="timetable-wrap">
            <table className="timetable">
              <thead>
                <tr>
                  <th>Time</th>
                  {DAYS.map((d) => <th key={d}>{d}</th>)}
                </tr>
              </thead>
              <tbody>
                {ALL_TIMES_ONLINE.map((time) => (
                  <tr key={time}>
                    <td className="time-cell">{time}</td>
                    {DAYS.map((day) => {
                      const dayData = ONLINE_SCHEDULE.find((s) => s.day === day);
                      const hasSession = dayData?.times.includes(time);
                      return (
                        <td key={day} className={hasSession ? 'session-yes' : 'session-no'}>
                          {hasSession ? <span className="dot" /> : <span className="dash">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* In-Person Sessions */}
      <section className="schedule-section alt-bg">
        <div className="container">
          <div className="sched-header">
            <div>
              <span className="section-label">In-Person Members</span>
              <div className="lime-line" />
              <h2 className="schedule-title">In-Person Sessions</h2>
              <p className="text-muted">Parkington Plaza, Maths Unlimited Building, Kingston.</p>
            </div>
            <div className="sched-note">
              <Info size={14} /> No 5am on Wed · No 6pm or 7:15pm on Wed & Fri
            </div>
          </div>
          <div className="timetable-wrap">
            <table className="timetable">
              <thead>
                <tr>
                  <th>Time</th>
                  {DAYS.map((d) => <th key={d}>{d}</th>)}
                </tr>
              </thead>
              <tbody>
                {ALL_TIMES_INPERSON.map((time) => (
                  <tr key={time}>
                    <td className="time-cell">{time}</td>
                    {DAYS.map((day) => {
                      const dayData = INPERSON_SCHEDULE.find((s) => s.day === day);
                      const hasSession = dayData?.times.includes(time);
                      return (
                        <td key={day} className={hasSession ? 'session-yes' : 'session-no'}>
                          {hasSession ? <span className="dot" /> : <span className="dash">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="sessions-per-week">
            <strong className="text-lime">Up to 9 sessions per week</strong> available for In-Person members.
          </div>
        </div>
      </section>

      {/* Drop-In Classes */}
      <section className="schedule-section">
        <div className="container">
          <span className="section-label">No Commitment Needed</span>
          <div className="lime-line" />
          <h2 className="schedule-title">Drop-In Classes</h2>
          <p className="text-muted" style={{ marginBottom: '32px' }}>
            $1,500 JMD per class. Show up whenever you can.
          </p>
          <div className="dropin-grid">
            {DROPIN_CLASSES.map((cls) => (
              <div className="dropin-card" key={cls.name}>
                <div className="dropin-icon">{cls.icon}</div>
                <div className="dropin-info">
                  <h3>{cls.name}</h3>
                  <p className="dropin-when">
                    <strong>{cls.day}</strong> · {cls.time}
                  </p>
                  <p className="dropin-price text-lime">$1,500 JMD</p>
                </div>
                <Link to={`/contact?package=dropin`} className="btn btn-ghost btn-sm">
                  Book <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legend */}
      <section className="schedule-legend">
        <div className="container legend-inner">
          <div className="legend-item"><span className="dot" /> Session available</div>
          <div className="legend-item"><span className="dash">—</span> No session</div>
          <div className="legend-item"><Info size={14} /> Check notes for exceptions</div>
        </div>
      </section>

      {/* CTA */}
      <section className="schedule-cta">
        <div className="container schedule-cta-inner">
          <h2>Ready to Start?</h2>
          <p>Pick a package and book your first session today.</p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/packages" className="btn btn-primary btn-lg">
              View Packages <ArrowRight size={18} />
            </Link>
            <Link to="/contact" className="btn btn-ghost btn-lg">
              Book a Session
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
